import React, { useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import { Rating } from "react-simple-star-rating";
import TestimonialAPI from "../../api/TestimonialApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import ImageUploader from "../../components/partials/ImageUploader";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SubmitButton from "../../components/partials/global/SubmitButton";

function AddNew() {
  useAlphabeticInput();

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const INITIAL_FORM_DATA = {
    customer_name: "",
    designation: "",
    company_name: "",
    message: "",
    rating: 5,
    image: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleRating = (rate) => {
    setFormData((prev) => ({
      ...prev,
      rating: rate,
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_name) {
      newErrors.customer_name = "Name Field is required";
    }

    if (!formData.designation) {
      newErrors.designation = "Designation Field is required";
    }

    if (!formData.message) {
      newErrors.message = "Message Field is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    showLoader();

    try {
      const res = await TestimonialAPI.addDetails(formData);

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add New Testimonial</h4>
            <h6>
              Create a new customer testimonial to showcase positive feedback on
              your store.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="User Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="testimonials"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Customer Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control only-alphabets"
                        placeholder="Customer Name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.customer_name && (
                      <span className="errors text-danger">
                        {errors.customer_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Designation <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-pencil"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control only-alphabets"
                        placeholder="Designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.designation && (
                      <span className="errors text-danger">
                        {errors.designation}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Company Name</label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-building"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Company Name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyName && (
                      <span className="errors text-danger">
                        {errors.companyName}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Rating</label>
                    <div>
                      <Rating
                        onClick={handleRating}
                        initialValue={formData.rating}
                      />
                    </div>
                    {errors.rating && (
                      <span className="errors text-danger">
                        {errors.rating}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">
                    Message <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-comment"></i>
                    </span>
                    <textarea
                      value={formData.message}
                      className="form-control"
                      name="message"
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  {errors.message && (
                    <span className="errors text-danger">{errors.message}</span>
                  )}
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Submit Details"
                    loadingText="Submitting..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
